import {makeAutoObservable, observable, runInAction, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import {DateTime} from "luxon";
import {first, last, sumBy, times} from "lodash";
import {Empty} from "data/types/global";
import {ContestStatus, RequestState, ModalType} from "data/enums";

import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IContestsStore, IContest} from "data/stores/contests/contestsStore";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {
	IRoundSummaryStore,
	IContestSummary,
} from "data/stores/round_summary/round_summary.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage, trackSentryErrors} from "data/utils";
import {GAME_TIMEZONE} from "data/constants";
import {LevelAccordions} from "data/enums/leagues";
import {AdobeDTM} from "data/utils/AdobeDTM";

interface IContestWithSummary {
	contest: Empty<IContest>;
	summary: Empty<IContestSummary>;
}

export interface ILeagueLevelsController extends ViewController {
	i18n: ILocalizationStore;

	get isLoading(): boolean;
	get weeklyPointsStatus(): ContestStatus;
	get roundContests(): IContestWithSummary[];
	get daysString(): string;
	get weeklyPoints(): number;

	isExpanded: (section: LevelAccordions) => boolean;
	toggleAccordion: (section: LevelAccordions) => void;
}

@injectable()
export class LeagueLevelsController implements ILeagueLevelsController {
	@observable private _requestState = RequestState.IDLE;
	@observable private _disposers: ReturnType<typeof reaction>[] = [];
	@observable private _accordionStates: Record<LevelAccordions, boolean> = {
		[LevelAccordions.LEADERBOARD]: false,
		[LevelAccordions.SUMMARY]: false,
		[LevelAccordions.PROMOTIONS]: true,
	};

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) private readonly _contestsStore: IContestsStore,
		@inject(Bindings.RoundsStore) private readonly _roundsStore: IRoundsStore,
		@inject(Bindings.RoundSummaryStore) private readonly _roundSummaryStore: IRoundSummaryStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	isExpanded = (section: LevelAccordions) => {
		return this._accordionStates[section];
	};

	toggleAccordion = (section: LevelAccordions) => {
		this._accordionStates[section] = !this._accordionStates[section];
	};
	get isLoading() {
		return this._requestState === RequestState.PENDING;
	}

	get roundContests() {
		const round = this.currentRound?.id;
		if (!round) {
			return times(7, (n) => n).map((n) => {
				return {
					contest: undefined,
					summary: undefined,
				} as IContestWithSummary;
			});
		}

		return this.contests
			.filter((contest) => contest.roundId === round)
			.map((contest) => {
				return {
					contest,
					summary: this._roundSummaryStore.getSummaryByContestId(contest.id),
				};
			});
	}

	get weeklyPointsStatus() {
		const hasLive = this.roundContests.some((it) => it.contest?.status === ContestStatus.Live);

		if (hasLive) {
			return ContestStatus.Live;
		}

		const hasComplete = this.roundContests.some(
			(it) => it.contest?.status === ContestStatus.Complete
		);

		if (hasComplete) {
			return ContestStatus.Complete;
		}

		return ContestStatus.Open;
	}

	get daysString() {
		const firstDay = this.contestMonthDay(first(this.roundContests)?.contest);
		const lastDay = this.contestMonthDay(last(this.roundContests)?.contest);
		if (!firstDay || !lastDay) {
			return "";
		}
		const firstDate = `${firstDay.month}/${firstDay.day}`;
		const lastDate =
			firstDay.month === lastDay.month ? `${lastDay.day}` : `${lastDay.month}/${lastDay.day}`;

		return `${firstDate}-${lastDate}`;
	}

	get weeklyPoints(): number {
		return sumBy(this.roundContests, (it) => it.summary?.ranking?.points || 0);
	}

	contestMonthDay(contest?: IContest | null) {
		if (!contest) {
			return null;
		}

		const contestDate = DateTime.fromISO(contest.date).setZone(GAME_TIMEZONE);
		if (contestDate.isValid) {
			return {
				month: contestDate.toFormat("L"),
				day: contestDate.toFormat("dd"),
			};
		}
		return null;
	}

	init(param: void) {
		this._disposers = [
			reaction(
				() => this.currentRound?.id,
				() => {
					void this.fetchData();
				},
				{fireImmediately: true}
			),
		];

		AdobeDTM.trackState({
			"page.legacyName": "Major League Baseball: Spinball: League",
		});
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
	}

	private async fetchData() {
		try {
			this._requestState = RequestState.PENDING;
			const roundId = this.currentRound?.id;
			if (roundId) {
				await this._roundSummaryStore.fetchSummary(roundId);
			}

			runInAction(() => {
				this._requestState = RequestState.SUCCESS;
			});
		} catch (e) {
			runInAction(() => {
				this._requestState = RequestState.ERROR;
			});
			this._modalsStore.showModal(ModalType.ERROR, {
				message: extractErrorMessage(e),
			});
			trackSentryErrors(e, {}, "_roundSummaryStore.fetchSummary");
		}
	}
	private get contests() {
		return this._contestsStore.list;
	}

	private get currentRound() {
		return this._roundsStore.activeRound;
	}
}
